import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useAuth } from '@/hooks/useAuth';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
	Copy,
	ExternalLink,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

interface AffiliateStatsProps {
	userId?: string;
}

export function AffiliateStats({ userId }: AffiliateStatsProps) {
	const { user } = useAuth();
	const targetUserId = userId || user?._id;

	const stats = useQuery(
		api.affiliate.getAffiliateStats,
		targetUserId ? { userId: targetUserId } : 'skip'
	);

	const copyAffiliateLink = () => {
		if (user?.gameId) {
			const affiliateLink = `https://${user.gameId}.watchersociety.com`;
			navigator.clipboard.writeText(affiliateLink);
			toast({
				title: 'Copied!',
				description: 'Affiliate link copied to clipboard',
				variant: 'default',
			});
		}
	};

	const openAffiliateLink = () => {
		if (user?.gameId) {
			const affiliateLink = `https://${user.gameId}.watchersociety.com`;
			window.open(affiliateLink, '_blank');
		}
	};

	if (!targetUserId) {
		return (
			<Card>
				<CardContent className='pt-6'>
					<p className='text-muted-foreground'>
						Please log in to view affiliate stats
					</p>
				</CardContent>
			</Card>
		);
	}

	if (stats === undefined) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>
						<Skeleton className='h-6 w-32' />
					</CardTitle>
					<CardDescription>
						<Skeleton className='h-4 w-48' />
					</CardDescription>
				</CardHeader>
				<CardContent className='space-y-4'>
					<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
						{[...Array(4)].map((_, i) => (
							<div key={i} className='space-y-2'>
								<Skeleton className='h-4 w-16' />
								<Skeleton className='h-8 w-12' />
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className='space-y-4 mt-6'>
			{/* Affiliate Link Section */}
			{user?.gameId && (
				<Card>
					<CardHeader>
						<CardTitle className='flex items-center gap-2'>
							<ExternalLink className='h-5 w-5' />
							Your Affiliate Link
						</CardTitle>
						<CardDescription>
							Share this link to earn referral rewards
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className='flex items-center gap-2'>
							<code className='flex-1 p-2 bg-muted rounded text-sm'>
								{user.gameId}.watchersociety.com
							</code>
							<Button
								variant='outline'
								size='sm'
								onClick={copyAffiliateLink}
							>
								<Copy className='h-4 w-4' />
							</Button>
							<Button
								variant='outline'
								size='sm'
								onClick={openAffiliateLink}
							>
								<ExternalLink className='h-4 w-4' />
							</Button>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
